import { css } from '@firebolt-dev/css'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronUpIcon, LoaderIcon, MessageSquareTextIcon, MicIcon, RefreshCwIcon, SendHorizonalIcon, XIcon } from 'lucide-react'
import moment from 'moment'

import { AvatarPane } from './AvatarPane'
import { AvatarChooser } from './AvatarChooser'
import { useElemSize } from './useElemSize'
import { MouseLeftIcon } from './MouseLeftIcon'
import { MouseRightIcon } from './MouseRightIcon'
import { MouseWheelIcon } from './MouseWheelIcon'
import { buttons, propToLabel } from '../../core/extras/buttons'
import { cls, isTouch } from '../utils'
import { uuid } from '../../core/utils'
import { ControlPriorities } from '../../core/extras/ControlPriorities'
import { storage } from '../../core/storage'
// import { AppsPane } from './AppsPane'
// import { MenuMain } from './MenuMain'
// import { MenuApp } from './MenuApp'
import { ChevronDoubleUpIcon, HandIcon, VRHeadsetIcon } from './Icons'
import { Sidebar } from './Sidebar'
import { ElevatorPanel } from './ElevatorPanel'
import { CourseRoomPanel } from './CourseRoomPanel'
import { XRHudSurface, useXRSession } from './XRHudSurface'
import { XR_HTML_PLACEMENT } from '../../core/extras/xrHtmlBridge'

export function CoreUI({ world }) {
  const ref = useRef()
  const [ready, setReady] = useState(false)
  const [player, setPlayer] = useState(() => world.entities.player)
  const [ui, setUI] = useState(world.ui.state)
  const [menu, setMenu] = useState(null)
  const [confirm, setConfirm] = useState(null)
  const [code, setCode] = useState(false)
  const [avatar, setAvatar] = useState(null)
  const [receptionistChat, setReceptionistChat] = useState({ active: false, nearby: false, bubbles: [], busy: false })
  const [needsAvatarSelection, setNeedsAvatarSelection] = useState(false)
  const [disconnected, setDisconnected] = useState(false)
  const [apps, setApps] = useState(false)
  const [kicked, setKicked] = useState(null)
  useEffect(() => {
    world.on('ready', setReady)
    world.on('player', setPlayer)
    world.on('ui', setUI)
    world.on('menu', setMenu)
    world.on('confirm', setConfirm)
    world.on('code', setCode)
    world.on('apps', setApps)
    world.on('avatar', setAvatar)
    world.on('receptionist-chat', setReceptionistChat)
    world.on('kick', setKicked)
    world.on('disconnect', setDisconnected)
    return () => {
      world.off('ready', setReady)
      world.off('player', setPlayer)
      world.off('ui', setUI)
      world.off('menu', setMenu)
      world.off('confirm', setConfirm)
      world.off('code', setCode)
      world.off('apps', setApps)
      world.off('avatar', setAvatar)
      world.off('receptionist-chat', setReceptionistChat)
      world.off('kick', setKicked)
      world.off('disconnect', setDisconnected)
    }
  }, [])

  useEffect(() => {
    if (ready) setNeedsAvatarSelection(!!world.network.needsAvatarSelection)
  }, [ready, world])

  useEffect(() => {
    const elem = ref.current
    const onEvent = e => {
      e.isCoreUI = true
    }
    elem.addEventListener('wheel', onEvent)
    elem.addEventListener('click', onEvent)
    elem.addEventListener('pointerdown', onEvent)
    elem.addEventListener('pointermove', onEvent)
    elem.addEventListener('pointerup', onEvent)
    elem.addEventListener('touchstart', onEvent)
    // elem.addEventListener('touchmove', onEvent)
    // elem.addEventListener('touchend', onEvent)
  }, [])
  useEffect(() => {
    document.documentElement.style.fontSize = `${16 * world.prefs.ui}px`
    function onChange(changes) {
      if (changes.ui) {
        document.documentElement.style.fontSize = `${16 * world.prefs.ui}px`
      }
    }
    world.prefs.on('change', onChange)
    return () => {
      world.prefs.off('change', onChange)
    }
  }, [])
  return (
    <div
      ref={ref}
      className='coreui'
      css={css`
        position: absolute;
        inset: 0;
        overflow: hidden;
      `}
    >
      {disconnected && <Disconnected />}
      {!ui.reticleSuppressors && <Reticle world={world} />}
      {<Toast world={world} />}
      {ready && <ActionsBlock world={world} />}
      {ready && <Sidebar world={world} ui={ui} />}
      {ready && world.xr.supportsVR && <EnterVRButton world={world} />}
      {ready && !needsAvatarSelection && !receptionistChat.active && <ElevatorPanel world={world} />}
      {ready && !needsAvatarSelection && !receptionistChat.active && <CourseRoomPanel world={world} />}
      {ready && !receptionistChat.active && <Chat world={world} />}
      {ready && receptionistChat.nearby && !receptionistChat.active && (
        <ReceptionistTalkButton world={world} />
      )}
      {ready && (
        <ReceptionistChat
          world={world}
          active={receptionistChat.active}
          bubbles={receptionistChat.bubbles || []}
          busy={receptionistChat.busy}
        />
      )}
      {/* {ready && <Side world={world} player={player} menu={menu} />} */}
      {avatar && <AvatarPane key={avatar.hash} world={world} info={avatar} />}
      {ready && (needsAvatarSelection || (ui.active && ui.pane === 'avatar')) && (
        <AvatarChooser
          world={world}
          required={needsAvatarSelection}
          onClose={() => {
            if (world.ui.state.pane === 'avatar') world.ui.togglePane(null)
          }}
          onSelected={() => {
            setNeedsAvatarSelection(false)
            if (world.ui.state.pane === 'avatar') world.ui.togglePane(null)
          }}
        />
      )}
      {/* {apps && <AppsPane world={world} close={() => world.ui.toggleApps()} />} */}
      {!ready && <LoadingOverlay world={world} />}
      {kicked && <KickedOverlay code={kicked} />}
      {ready && isTouch && <TouchBtns world={world} />}
      {ready && isTouch && <TouchStick world={world} />}
      {confirm && <Confirm options={confirm} />}
      <div id='core-ui-portal' />
    </div>
  )
}

// Large VR entry, top right: clear of Course Room, Smily and touch buttons below it.
function EnterVRButton({ world }) {
  const session = useXRSession(world)
  if (session) return null
  return (
    <button
      type='button'
      aria-label='Enter VR'
      onClick={() => world.xr.enter()}
      css={css`
        position: absolute;
        top: calc(1rem + env(safe-area-inset-top));
        right: calc(1rem + env(safe-area-inset-right));
        z-index: 36;
        min-height: 3.5rem;
        padding: 0.625rem 1.375rem 0.625rem 1rem;
        display: flex;
        align-items: center;
        gap: 0.75rem;
        border: 2px solid rgba(255, 255, 255, 0.85);
        border-radius: 1.75rem;
        color: white;
        background: rgba(11, 10, 21, 0.94);
        box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.35);
        font-family: 'Rubik', sans-serif;
        font-size: 1.25rem;
        font-weight: 600;
        line-height: 1;
        white-space: nowrap;
        cursor: pointer;
        pointer-events: auto;
        touch-action: manipulation;
        &:hover {
          background: rgba(36, 54, 75, 0.98);
        }
      `}
    >
      <VRHeadsetIcon size='2rem' />
      Enter VR
    </button>
  )
}

function ReceptionistTalkButton({ world }) {
  const session = useXRSession(world)
  if (session) return null // XR uses the button placed at the receptionist.
  return (
    <XRHudSurface world={world} placement={XR_HTML_PLACEMENT.bottomCenter}>
    <button
      type='button'
      aria-label='Talk with receptionist'
      onClick={() => world.receptionist.startConversation()}
      css={css`
        position: absolute;
        left: 50%;
        bottom: 6.25rem;
        z-index: 30;
        min-height: 3rem;
        padding: 0.75rem 1.125rem;
        transform: translateX(-50%);
        border: 1px solid rgba(255, 255, 255, 0.18);
        border-radius: 1.5rem;
        color: white;
        background: rgba(11, 10, 21, 0.94);
        box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.3);
        font: inherit;
        font-weight: 600;
        white-space: nowrap;
        pointer-events: auto;
        touch-action: manipulation;

        .receptionist-talk-key {
          min-width: 1.5rem;
          height: 1.5rem;
          margin-right: 0.5rem;
          padding: 0 0.375rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(255, 255, 255, 0.35);
          border-radius: 0.375rem;
          background: rgba(255, 255, 255, 0.08);
          font-size: 0.75rem;
          line-height: 1;
        }
      `}
    >
      <span className='receptionist-talk-key'>E</span>
      Talk with receptionist
    </button>
    </XRHudSurface>
  )
}

function cancelReceptionistRecording(recordingRef, timerRef, requestRef, generationRef) {
  generationRef.current++
  clearTimeout(timerRef.current)
  const recording = recordingRef.current
  recordingRef.current = null
  if (recording) {
    recording.discard = true
    if (recording.recorder.state !== 'inactive') recording.recorder.stop()
    recording.stream.getTracks().forEach(track => track.stop())
  }
  requestRef.current?.abort()
  requestRef.current = null
}

function ReceptionistChat({ world, active, bubbles, busy }) {
  const [value, setValue] = useState('')
  const [listening, setListening] = useState(false)
  const [transcribing, setTranscribing] = useState(false)
  const [micError, setMicError] = useState('')
  const inputRef = useRef()
  const bubblesRef = useRef()
  const recordingRef = useRef(null)
  const recordingTimerRef = useRef(null)
  const requestRef = useRef(null)
  const micGenerationRef = useRef(0)
  const activeRef = useRef(active)
  activeRef.current = active
  const xr = useXRSession(world)
  const micAvailable = typeof window !== 'undefined' && window.isSecureContext &&
    !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder

  useEffect(() => {
    if (active) return
    cancelReceptionistRecording(recordingRef, recordingTimerRef, requestRef, micGenerationRef)
    setListening(false)
    setTranscribing(false)
    setMicError('')
  }, [active])

  useEffect(() => () => {
    cancelReceptionistRecording(recordingRef, recordingTimerRef, requestRef, micGenerationRef)
  }, [])

  useEffect(() => {
    if (!active) return
    inputRef.current?.focus()
  }, [active])

  useEffect(() => {
    const elem = bubblesRef.current
    if (elem) elem.scrollTop = elem.scrollHeight
  }, [bubbles])

  if (!active) return null

  const send = () => {
    const text = value.trim()
    if (!text || busy) return
    cancelReceptionistRecording(recordingRef, recordingTimerRef, requestRef, micGenerationRef)
    setListening(false)
    setTranscribing(false)
    world.receptionist.sendMessage(text)
    setValue('')
  }

  const stopRecording = () => {
    const recording = recordingRef.current
    if (!recording || recording.recorder.state === 'inactive') return
    clearTimeout(recordingTimerRef.current)
    recording.recorder.stop()
    recording.stream.getTracks().forEach(track => track.stop())
    setListening(false)
    setTranscribing(true)
  }

  const toggleMic = async () => {
    if (recordingRef.current) return stopRecording()
    if (!micAvailable || busy || transcribing) return
    const generation = ++micGenerationRef.current
    setMicError('')
    world.receptionist.stopSpeech()
    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
      if (!activeRef.current || generation !== micGenerationRef.current) {
        stream.getTracks().forEach(track => track.stop())
        return
      }
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
        .find(type => MediaRecorder.isTypeSupported(type))
      if (!mimeType) throw new Error('unsupported-format')
      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64000 })
      const recording = { recorder, stream, chunks: [], discard: false }
      recordingRef.current = recording
      recorder.ondataavailable = event => {
        if (event.data.size) recording.chunks.push(event.data)
      }
      recorder.onerror = () => {
        cancelReceptionistRecording(recordingRef, recordingTimerRef, requestRef, micGenerationRef)
        setListening(false)
        setTranscribing(false)
        setMicError('Microphone recording failed. Please try again.')
      }
      recorder.onstop = async () => {
        if (recording.discard || recordingRef.current !== recording) return
        recordingRef.current = null
        const audio = new Blob(recording.chunks, { type: recorder.mimeType })
        if (audio.size < 64 || audio.size > 1_000_000) {
          setTranscribing(false)
          setMicError('Recording is empty or too long. Please try again.')
          return
        }
        const controller = new AbortController()
        requestRef.current = controller
        try {
          const bytes = new Uint8Array(await audio.arrayBuffer())
          let binary = ''
          for (const byte of bytes) binary += String.fromCharCode(byte)
          if (!activeRef.current || controller.signal.aborted) return
          const response = await fetch(`${world.network.apiUrl}/receptionist/transcribe`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${storage.get('authToken')}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ audio: btoa(binary), mimeType: recorder.mimeType }),
            signal: controller.signal,
          })
          const result = await response.json()
          if (!activeRef.current || controller.signal.aborted) return
          if (!response.ok) throw new Error(result.error || 'Speech transcription failed. Please try again.')
          const transcript = result.transcript?.trim()
          if (!transcript) throw new Error('No words recognized. Please try again.')
          setValue('')
          world.receptionist.sendMessage(transcript)
        } catch (error) {
          if (!controller.signal.aborted && activeRef.current) setMicError(error.message || 'Speech transcription failed. Please try again.')
        } finally {
          if (requestRef.current === controller) requestRef.current = null
          if (activeRef.current) setTranscribing(false)
        }
      }
      recorder.start()
      setListening(true)
      recordingTimerRef.current = setTimeout(stopRecording, 15000)
    } catch (error) {
      stream?.getTracks().forEach(track => track.stop())
      if (!activeRef.current || generation !== micGenerationRef.current) return
      recordingRef.current = null
      setListening(false)
      const messages = {
        NotAllowedError: 'Microphone permission denied. Check Chrome site settings.',
        NotFoundError: 'No microphone is available.',
        NotReadableError: 'The microphone is being used by another app.',
      }
      setMicError(messages[error.name] || 'Could not start the microphone. Please try again.')
    }
  }

  return (
    <XRHudSurface world={world} placement={XR_HTML_PLACEMENT.center}>
    <div
      className={xr ? 'receptionist-conversation xr' : 'receptionist-conversation'}
      css={css`
        /* XR HUD copy: fixed pixel layout, identical in the page and in its rasterised image. */
        &.xr {
          position: relative;
          width: 680px;
          height: 540px;
        }
        &.xr .receptionist-bubbles {
          top: 16px;
          left: 16px;
          right: 16px;
          max-height: 380px;
        }
        &.xr .receptionist-bubble {
          max-width: 420px;
        }
        &.xr .receptionist-chat {
          width: 600px;
          bottom: 16px;
        }
        .receptionist-bubbles {
          position: absolute;
          top: 16%;
          left: clamp(1rem, 7vw, 6rem);
          right: clamp(1rem, 7vw, 6rem);
          max-height: 52vh;
          z-index: 29;
          padding: 0.25rem 0.75rem 0.25rem 0.25rem;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.625rem;
          overflow-x: hidden;
          overflow-y: auto;
          overscroll-behavior: contain;
          pointer-events: auto;
          scrollbar-width: thin;
        }
        .receptionist-bubble {
          position: relative;
          flex: 0 0 auto;
          width: fit-content;
          max-width: min(18rem, 40vw);
          padding: 0.75rem 0.875rem;
          color: rgba(255, 255, 255, 1);
          background: rgba(11, 10, 21, 0.72);
          border: 1px solid rgba(255, 255, 255, 0.16);
          border-radius: 1rem;
          font-weight: 600;
          line-height: 1.35;
          overflow-wrap: anywhere;
          box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.28);
        }
        .receptionist-bubble--user {
          align-self: flex-end;
          background: rgba(27, 49, 72, 0.82);
          border-color: rgba(177, 216, 255, 0.3);
        }
        .receptionist-chat {
          position: absolute;
          left: 50%;
          bottom: 1.5rem;
          width: min(32rem, calc(100% - 2rem));
          transform: translateX(-50%);
          z-index: 30;
          display: flex;
          overflow: visible;
          color: white;
          background: rgba(11, 10, 21, 0.985);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 0.875rem;
          box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.35);
          pointer-events: auto;
        }

        .receptionist-chat-close,
        .receptionist-chat-mic,
        .receptionist-chat-send {
          width: 2.5rem;
          height: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 0;
          color: inherit;
          background: transparent;
          cursor: pointer;
        }
        .receptionist-chat-entry {
          width: 100%;
          min-height: 3.25rem;
          padding-left: 0.25rem;
          display: flex;
          align-items: center;
        }
        .receptionist-chat-entry input {
          min-width: 0;
          flex: 1;
          color: white;
          font: inherit;
        }
        .receptionist-chat-send {
          margin-left: auto;
          flex: 0 0 2.5rem;
        }
        .receptionist-chat-mic[aria-pressed='true'] {
          color: #ff7575;
        }
        .receptionist-chat-mic:disabled {
          opacity: 0.4;
          cursor: default;
        }
        .receptionist-chat-status {
          position: absolute;
          right: 0;
          bottom: calc(100% + 0.375rem);
          max-width: 100%;
          padding: 0.3rem 0.5rem;
          border-radius: 0.375rem;
          color: white;
          background: rgba(11, 10, 21, 0.9);
          font-size: 0.8rem;
        }

        @media (max-width: 600px) {
          .receptionist-bubbles {
            top: 10%;
            left: 0.5rem;
            right: 0.5rem;
            max-height: 48vh;
          }
          .receptionist-bubble {
            max-width: min(15rem, 58vw);
          }
        }
      `}
    >
      <div ref={bubblesRef} className='receptionist-bubbles' aria-live='polite'>
        {bubbles.map((bubble, index) => (
          <div
            className={bubble.role === 'user' ? 'receptionist-bubble receptionist-bubble--user' : 'receptionist-bubble'}
            key={index}
          >
            {bubble.text}
          </div>
        ))}
      </div>
      <section className='receptionist-chat'>
        {(listening || transcribing || micError) && <div className='receptionist-chat-status' role='status'>{micError || (transcribing ? 'Transcribing...' : 'Listening... Tap the microphone again to send.')}</div>}
        <div className='receptionist-chat-entry'>
          <button
            className='receptionist-chat-close'
            type='button'
            aria-label='Close conversation'
            onClick={() => {
              inputRef.current?.blur()
              cancelReceptionistRecording(recordingRef, recordingTimerRef, requestRef, micGenerationRef)
              world.receptionist.stopConversation()
            }}
          >
            <XIcon size='1.125rem' />
          </button>
          <input
            ref={inputRef}
            type='text'
            placeholder={busy ? 'Thinking...' : 'Type your message...'}
            value={value}
            maxLength={500}
            disabled={busy}
            onChange={event => setValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') send()
              if (event.key === 'Escape') {
                inputRef.current?.blur()
                cancelReceptionistRecording(recordingRef, recordingTimerRef, requestRef, micGenerationRef)
                world.receptionist.stopConversation()
              }
            }}
          />
          <button
            className='receptionist-chat-mic'
            type='button'
            aria-label={listening ? 'Stop and transcribe' : 'Speak to receptionist'}
            aria-pressed={listening}
            title={!micAvailable ? 'Microphone requires a supported browser and HTTPS' : undefined}
            disabled={!micAvailable || busy || transcribing}
            onClick={toggleMic}
          >
            <MicIcon size='1.125rem' />
          </button>
          <button className='receptionist-chat-send' type='button' aria-label='Send message' disabled={busy} onClick={send}>
            <SendHorizonalIcon size='1.125rem' />
          </button>
        </div>
      </section>
    </div>
    </XRHudSurface>
  )
}

// function Side({ world, menu }) {
//   const inputRef = useRef()
//   const [msg, setMsg] = useState('')
//   const [chat, setChat] = useState(false)
//   const [livekit, setLiveKit] = useState(() => world.livekit.status)
//   const [actions, setActions] = useState(() => world.prefs.actions)
//   useEffect(() => {
//     const onPrefsChange = changes => {
//       if (changes.actions) setActions(changes.actions.value)
//     }
//     const onLiveKitStatus = status => {
//       setLiveKit({ ...status })
//     }
//     world.livekit.on('status', onLiveKitStatus)
//     world.prefs.on('change', onPrefsChange)
//     return () => {
//       world.prefs.off('change', onPrefsChange)
//       world.livekit.off('status', onLiveKitStatus)
//     }
//   }, [])
//   useEffect(() => {
//     const control = world.controls.bind({ priority: ControlPriorities.CORE_UI })
//     control.slash.onPress = () => {
//       if (!chat) setChat(true)
//     }
//     control.enter.onPress = () => {
//       if (!chat) setChat(true)
//     }
//     control.mouseLeft.onPress = () => {
//       if (control.pointer.locked && chat) {
//         setChat(false)
//       }
//     }
//     return () => control.release()
//   }, [chat])
//   useEffect(() => {
//     if (chat) {
//       inputRef.current.focus()
//     } else {
//       inputRef.current.blur()
//     }
//   }, [chat])
//   const send = async e => {
//     if (world.controls.pointer.locked) {
//       setTimeout(() => setChat(false), 10)
//     }
//     if (!msg) {
//       e.preventDefault()
//       return setChat(false)
//     }
//     setMsg('')
//     // check for commands
//     if (msg.startsWith('/')) {
//       world.chat.command(msg)
//       return
//     }
//     // otherwise post it
//     const player = world.entities.player
//     const data = {
//       id: uuid(),
//       from: player.data.name,
//       fromId: player.data.id,
//       body: msg,
//       createdAt: moment().toISOString(),
//     }
//     world.chat.add(data, true)
//     if (isTouch) {
//       e.target.blur()
//       // setTimeout(() => setChat(false), 10)
//     }
//   }
//   return (
//     <div
//       className='side'
//       css={css`
//         position: absolute;
//         top: calc(4rem + env(safe-area-inset-top));
//         left: calc(4rem + env(safe-area-inset-left));
//         bottom: calc(4rem + env(safe-area-inset-bottom));
//         right: calc(4rem + env(safe-area-inset-right));
//         display: flex;
//         align-items: stretch;
//         font-size: 1rem;
//         .side-content {
//           max-width: 21rem;
//           width: 100%;
//           display: flex;
//           flex-direction: column;
//           align-items: stretch;
//         }
//         .side-btns {
//           display: flex;
//           align-items: center;
//           margin-left: -0.5rem;
//         }
//         .side-btn {
//           pointer-events: auto;
//           /* margin-bottom: 1rem; */
//           width: 2.5rem;
//           height: 2.5rem;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           cursor: pointer;
//           svg {
//             filter: drop-shadow(0 0.0625rem 0.125rem rgba(0, 0, 0, 0.2));
//           }
//         }
//         .side-mid {
//           flex: 1;
//           display: flex;
//           flex-direction: column;
//           justify-content: center;
//         }
//         .side-chatbox {
//           margin-top: 0.5rem;
//           background: rgba(0, 0, 0, 0.3);
//           padding: 0.625rem;
//           display: flex;
//           align-items: center;
//           opacity: 0;
//           &.active {
//             opacity: 1;
//             pointer-events: auto;
//           }
//           &-input {
//             flex: 1;
//             /* paint-order: stroke fill; */
//             /* -webkit-text-stroke: 0.25rem rgba(0, 0, 0, 0.2); */
//             &::placeholder {
//               color: rgba(255, 255, 255, 0.5);
//             }
//           }
//         }
//         @media all and (max-width: 700px), (max-height: 700px) {
//           top: calc(1.5rem + env(safe-area-inset-top));
//           left: calc(1.5rem + env(safe-area-inset-left));
//           bottom: calc(1.5rem + env(safe-area-inset-bottom));
//           right: calc(1.5rem + env(safe-area-inset-right));
//         }
//       `}
//     >
//       <div className='side-content'>
//         <div className='side-btns'>
//           <div className='side-btn' onClick={() => world.ui.toggleMain()}>
//             <MenuIcon size='1.5rem' />
//           </div>
//           {isTouch && (
//             <div
//               className='side-btn'
//               onClick={() => {
//                 console.log('setChat', !chat)
//                 setChat(!chat)
//               }}
//             >
//               <ChatIcon size='1.5rem' />
//             </div>
//           )}
//           {livekit.connected && (
//             <div
//               className='side-btn'
//               onClick={() => {
//                 world.livekit.setMicrophoneEnabled()
//               }}
//             >
//               {livekit.mic ? <MicIcon size='1.5rem' /> : <MicOffIcon size='1.5rem' />}
//             </div>
//           )}
//           {world.xr.supportsVR && (
//             <div
//               className='side-btn'
//               onClick={() => {
//                 world.xr.enter()
//               }}
//             >
//               <VRIcon size='1.5rem' />
//             </div>
//           )}
//         </div>
//         {menu?.type === 'main' && <MenuMain world={world} />}
//         {menu?.type === 'app' && <MenuApp key={menu.app.data.id} world={world} app={menu.app} blur={menu.blur} />}
//         <div className='side-mid'>{!menu && !isTouch && actions && <Actions world={world} />}</div>
//         {isTouch && !chat && <MiniMessages world={world} />}
//         {(isTouch ? chat : true) && <Messages world={world} active={chat || menu} />}
//         <label className={cls('side-chatbox', { active: chat })}>
//           <input
//             ref={inputRef}
//             className='side-chatbox-input'
//             type='text'
//             placeholder='Say something...'
//             value={msg}
//             onChange={e => setMsg(e.target.value)}
//             onKeyDown={e => {
//               if (e.code === 'Escape') {
//                 setChat(false)
//               }
//               // meta quest 3 isn't spec complaint and instead has e.code = '' and e.key = 'Enter'
//               // spec says e.code should be a key code and e.key should be the text output of the key eg 'b', 'B', and '\n'
//               if (e.code === 'Enter' || e.key === 'Enter') {
//                 send(e)
//               }
//             }}
//             onBlur={e => {
//               if (!isTouch) {
//                 setChat(false)
//               }
//             }}
//           />
//         </label>
//       </div>
//     </div>
//   )
// }

function Chat({ world }) {
  const inputRef = useRef()
  const [msg, setMsg] = useState('')
  const [active, setActive] = useState(false)
  useEffect(() => {
    const onToggle = () => {
      setActive(value => !value)
    }
    world.on('sidebar-chat-toggle', onToggle)
    return () => {
      world.off('sidebar-chat-toggle', onToggle)
    }
  }, [])
  useEffect(() => {
    const control = world.controls.bind({ priority: ControlPriorities.CORE_UI })
    control.slash.onPress = () => {
      if (!active) setActive(true)
    }
    control.enter.onPress = () => {
      if (!active) setActive(true)
    }
    control.mouseLeft.onPress = () => {
      if (control.pointer.locked && active) {
        setActive(false)
      }
    }
    return () => control.release()
  }, [active])
  useEffect(() => {
    if (active) {
      inputRef.current.focus()
    } else {
      inputRef.current.blur()
    }
  }, [active])
  const send = async e => {
    if (world.controls.pointer.locked) {
      setTimeout(() => setActive(false), 10)
    }
    if (!msg) {
      e.preventDefault()
      return setActive(false)
    }
    setMsg('')
    // check for commands
    if (msg.startsWith('/')) {
      world.chat.command(msg)
      return
    }
    // otherwise post it
    world.chat.send(msg)
    if (isTouch) {
      // setActive(false)
      e.target.blur()
      setTimeout(() => setActive(false), 10)
    }
  }
  return (
    <div
      className={cls('mainchat', { active })}
      css={css`
        position: absolute;
        left: calc(2rem + env(safe-area-inset-left));
        bottom: calc(2rem + env(safe-area-inset-bottom));
        width: 20rem;
        font-size: 1rem;
        @media all and (max-width: 1200px) {
          left: calc(1rem + env(safe-area-inset-left));
          bottom: calc(1rem + env(safe-area-inset-bottom));
        }
        .mainchat-msgs {
          padding: 0 0 0.5rem 0.4rem;
        }
        .mainchat-btn {
          pointer-events: auto;
          width: 2.875rem;
          height: 2.875rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(11, 10, 21, 0.85);
          border: 0.0625rem solid #2a2b39;
          border-radius: 1rem;
          &:hover {
            cursor: pointer;
          }
          opacity: 0; // disabled
        }
        .mainchat-entry {
          height: 2.875rem;
          padding: 0 1rem;
          background: rgba(11, 10, 21, 0.9);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 2rem;
          display: flex;
          align-items: center;

          // debug
          display: none;
          /* pointer-events: auto;
          opacity: 1; */

          input {
            font-size: 0.9375rem;
            line-height: 1;
            &::selection {
              background-color: white;
              color: black;
            }
          }
        }
        .mainchat-send {
          width: 2.875rem;
          height: 2.875rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: -0.6rem;
        }
        &.active {
          pointer-events: auto;
          .mainchat-btn {
            display: none;
          }
          .mainchat-entry {
            display: flex;
          }
        }
      `}
    >
      <div className='mainchat-msgs'>
        {isTouch && !active && <MiniMessages world={world} />}
        {(!isTouch || active) && <Messages world={world} active={active} />}
      </div>
      <div
        className='mainchat-btn'
        onClick={() => {
          setActive(true)
        }}
      >
        <MessageSquareTextIcon size='1.125rem' />
      </div>
      <label className='mainchat-entry'>
        <input
          ref={inputRef}
          className='side-chatbox-input'
          type='text'
          placeholder='Say something...'
          value={msg}
          onChange={e => setMsg(e.target.value)}
          onKeyDown={e => {
            if (e.code === 'Escape') {
              setActive(false)
            }
            // meta quest 3 isn't spec complaint and instead has e.code = '' and e.key = 'Enter'
            // spec says e.code should be a key code and e.key should be the text output of the key eg 'b', 'B', and '\n'
            if (e.code === 'Enter' || e.key === 'Enter') {
              send(e)
            }
          }}
          onBlur={e => {
            if (!isTouch) {
              setActive(false)
            }
          }}
        />
        {isTouch && (
          <div className='mainchat-send' onClick={e => send(e)}>
            <SendHorizonalIcon size='1.125rem' />
          </div>
        )}
      </label>
    </div>
  )
}

function MiniMessages({ world }) {
  const [msg, setMsg] = useState(null)
  useEffect(() => {
    let init
    return world.chat.subscribe(msgs => {
      if (!init) {
        init = true
        return // skip first
      }
      const msg = msgs[msgs.length - 1]
      if (msg.fromId === world.network.id) return
      setMsg(msg)
    })
  }, [])
  useEffect(() => {
    const timerId = setTimeout(() => {
      setMsg(null)
    }, 4000)
    return () => clearTimeout(timerId)
  }, [msg])
  if (!msg) return null
  return <Message msg={msg} />
}

const MESSAGES_REFRESH_RATE = 30 // every x seconds

function Messages({ world, active }) {
  const initRef = useRef()
  const contentRef = useRef()
  const spacerRef = useRef()
  // const [now, setNow] = useState(() => moment())
  const [msgs, setMsgs] = useState([])
  useEffect(() => {
    return world.chat.subscribe(setMsgs)
  }, [])
  // useEffect(() => {
  //   let timerId
  //   const updateNow = () => {
  //     setNow(moment())
  //     timerId = setTimeout(updateNow, MESSAGES_REFRESH_RATE * 1000)
  //   }
  //   timerId = setTimeout(updateNow, MESSAGES_REFRESH_RATE * 1000)
  //   return () => clearTimeout(timerId)
  // }, [])
  useEffect(() => {
    if (!msgs.length) return
    const didInit = !initRef.current
    if (didInit) {
      spacerRef.current.style.height = contentRef.current.offsetHeight + 'px'
    }
    setTimeout(() => {
      contentRef.current?.scroll({
        top: 9999999,
        behavior: didInit ? 'instant' : 'smooth',
      })
    }, 10)
    initRef.current = true
  }, [msgs])
  useEffect(() => {
    const content = contentRef.current
    // const spacer = spacerRef.current
    // spacer.style.height = content.offsetHeight + 'px'
    const observer = new ResizeObserver(() => {
      contentRef.current?.scroll({
        top: 9999999,
        behavior: 'instant',
      })
    })
    observer.observe(content)
    return () => {
      observer.disconnect()
    }
  }, [])
  return (
    <div
      ref={contentRef}
      className={cls('messages noscrollbar', { active })}
      css={css`
        /* padding: 0 0 0.5rem; */
        /* margin-bottom: 20px; */
        flex: 1;
        max-height: 16rem;
        transition: all 0.15s ease-out;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        overflow-y: auto;
        -webkit-mask-image: linear-gradient(to top, black calc(100% - 10rem), black 10rem, transparent);
        mask-image: linear-gradient(to top, black calc(100% - 10rem), black 10rem, transparent);
        &.active {
          pointer-events: auto;
        }
        .messages-spacer {
          flex-shrink: 0;
        }
      `}
    >
      <div className='messages-spacer' ref={spacerRef} />
      {msgs.map(msg => (
        <Message key={msg.id} msg={msg} /*now={now}*/ />
      ))}
    </div>
  )
}

function Message({ msg, now }) {
  // const timeAgo = useMemo(() => {
  //   const createdAt = moment(msg.createdAt)
  //   const age = now.diff(createdAt, 'seconds')
  //   // up to 10s ago show now
  //   if (age < 10) return 'now'
  //   // under a minute show seconds
  //   if (age < 60) return `${age}s ago`
  //   // under an hour show minutes
  //   if (age < 3600) return Math.floor(age / 60) + 'm ago'
  //   // under a day show hours
  //   if (age < 86400) return Math.floor(age / 3600) + 'h ago'
  //   // otherwise show days
  //   return Math.floor(age / 86400) + 'd ago'
  // }, [now])
  return (
    <div
      className='message'
      css={css`
        padding: 0.25rem 0;
        line-height: 1.4;
        font-size: 1rem;
        paint-order: stroke fill;
        -webkit-text-stroke: 0.25rem rgba(0, 0, 0, 0.2);
        .message-from {
          margin-right: 0.25rem;
        }
        .message-body {
          // ...
        }
      `}
    >
      {msg.from && <span className='message-from'>[{msg.from}]</span>}
      <span className='message-body'>{msg.body}</span>
      {/* <span>{timeAgo}</span> */}
    </div>
  )
}

function Disconnected() {
  // useEffect(() => {
  //   document.body.style.filter = 'grayscale(100%)'
  //   return () => {
  //     document.body.style.filter = null
  //   }
  // }, [])
  return (
    <>
      <div
        css={css`
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          backdrop-filter: grayscale(100%);
          pointer-events: none;
          z-index: 9999;
          animation: fadeIn 3s forwards;
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
        `}
      />
      <div
        css={css`
          pointer-events: auto;
          position: absolute;
          top: 50%;
          left: 50%;
          background: rgba(11, 10, 21, 0.85);
          border: 0.0625rem solid #2a2b39;
          backdrop-filter: blur(5px);
          border-radius: 1rem;
          height: 2.75rem;
          padding: 0 1rem;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          cursor: pointer;
          > span {
            margin-left: 0.4rem;
          }
        `}
        onClick={() => window.location.reload()}
      >
        <RefreshCwIcon size='1.1rem' />
        <span>Reconnect</span>
      </div>
    </>
  )
}

function LoadingOverlay({ world }) {
  const [progress, setProgress] = useState(0)
  const [environmentError, setEnvironmentError] = useState(!!world.environment.loadingError)
  const { title, desc, image } = world.settings
  useEffect(() => {
    world.on('progress', setProgress)
    world.on('environmentError', setEnvironmentError)
    setEnvironmentError(!!world.environment.loadingError)
    return () => {
      world.off('progress', setProgress)
      world.off('environmentError', setEnvironmentError)
    }
  }, [world])
  return (
    <div
      css={css`
        position: absolute;
        inset: 0;
        background: black;
        display: flex;
        pointer-events: auto;
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
          }
        }
        .loading-image {
          position: absolute;
          inset: 0;
          background-position: center;
          background-size: cover;
          background-repeat: no-repeat;
          background-image: ${image ? `url(${world.resolveURL(image.url)})` : 'none'};
          animation: pulse 5s ease-in-out infinite;
        }
        .loading-shade {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(15px);
        }
        .loading-info {
          position: absolute;
          bottom: 50px;
          left: 50px;
          right: 50px;
          max-width: 28rem;
        }
        .loading-title {
          font-size: 2.4rem;
          line-height: 1.2;
          font-weight: 600;
          margin: 0 0 0.5rem;
        }
        .loading-desc {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
          margin: 0 0 20px;
        }
        .loading-track {
          height: 5px;
          border-radius: 3px;
          background: rgba(255, 255, 255, 0.1);
          position: relative;
        }
        .loading-bar {
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          width: ${progress}%;
          background: white;
          border-radius: 3px;
          transition: width 0.2s ease-out;
        }
      `}
    >
      <div className='loading-image' />
      <div className='loading-shade' />
      <div className='loading-info'>
        {title && <div className='loading-title'>{title}</div>}
        {desc && <div className='loading-desc'>{desc}</div>}
        {environmentError ? (
          <div role='alert' className='loading-desc'>
            Unable to load the room. Please reload to try again.
            <div>
              <button onClick={() => window.location.reload()}>Reload</button>
            </div>
          </div>
        ) : (
          <div className='loading-track'>
            <div className='loading-bar' />
          </div>
        )}
      </div>
    </div>
  )
}

const kickMessages = {
  duplicate_user: 'Player already active on another device or window.',
  player_limit: 'Player limit reached.',
  unknown: 'You were kicked.',
}
function KickedOverlay({ code }) {
  return (
    <div
      css={css`
        position: absolute;
        inset: 0;
        background: black;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: auto;
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        svg {
          animation: spin 1s linear infinite;
        }
      `}
    >
      <div>{kickMessages[code] || kickMessages.unknown}</div>
    </div>
  )
}

function ActionsBlock({ world }) {
  const [showActions, setShowActions] = useState(() => world.prefs.actions)
  useEffect(() => {
    const onPrefsChange = changes => {
      if (changes.actions) setShowActions(changes.actions.value)
    }
    world.prefs.on('change', onPrefsChange)
    return () => {
      world.prefs.off('change', onPrefsChange)
    }
  }, [])
  if (isTouch) return null
  if (!showActions) return null
  return (
    <div
      css={css`
        position: absolute;
        top: calc(2rem + env(safe-area-inset-top));
        left: calc(2rem + env(safe-area-inset-left));
        bottom: calc(2rem + env(safe-area-inset-bottom));
        display: flex;
        flex-direction: column;
        align-items: center;
        @media all and (max-width: 1200px) {
          top: calc(1rem + env(safe-area-inset-top));
          left: calc(1rem + env(safe-area-inset-left));
          bottom: calc(1rem + env(safe-area-inset-bottom));
        }
      `}
    >
      <Actions world={world} />
    </div>
  )
}

function Actions({ world }) {
  const [actions, setActions] = useState(() => world.controls.actions)
  useEffect(() => {
    world.on('actions', setActions)
    return () => world.off('actions', setActions)
  }, [])
  return (
    <div
      className='actions'
      css={css`
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        .actions-item {
          display: flex;
          align-items: center;
          margin: 0 0 0.5rem;
          &-icon {
            // ...
          }
          &-label {
            margin-left: 0.625em;
            paint-order: stroke fill;
            -webkit-text-stroke: 0.25rem rgba(0, 0, 0, 0.2);
          }
        }
      `}
    >
      {actions.map(action => (
        <div className='actions-item' key={action.id}>
          <div className='actions-item-icon'>{getActionIcon(action)}</div>
          <div className='actions-item-label'>{action.label}</div>
        </div>
      ))}
    </div>
  )
}

function getActionIcon(action) {
  if (action.type === 'custom') {
    return <ActionPill label={action.btn} />
  }
  if (action.type === 'controlLeft') {
    return <ActionPill label='Ctrl' />
  }
  if (action.type === 'mouseLeft') {
    return <ActionIcon icon={MouseLeftIcon} />
  }
  if (action.type === 'mouseRight') {
    return <ActionIcon icon={MouseRightIcon} />
  }
  if (action.type === 'mouseWheel') {
    return <ActionIcon icon={MouseWheelIcon} />
  }
  if (buttons.has(action.type)) {
    return <ActionPill label={propToLabel[action.type]} />
  }
  return <ActionPill label='?' />
}

function ActionPill({ label }) {
  return (
    <div
      className='actionpill'
      css={css`
        border: 0.0625rem solid white;
        border-radius: 0.25rem;
        background: rgba(0, 0, 0, 0.1);
        padding: 0.25rem 0.375rem;
        font-size: 0.875em;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        paint-order: stroke fill;
        -webkit-text-stroke: 0.25rem rgba(0, 0, 0, 0.2);
      `}
    >
      {label}
    </div>
  )
}

function ActionIcon({ icon: Icon }) {
  return (
    <div
      className='actionicon'
      css={css`
        line-height: 0;
        svg {
          filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.8));
        }
      `}
    >
      <Icon size='1.5rem' />
    </div>
  )
}

function Reticle({ world }) {
  const [pointerLocked, setPointerLocked] = useState(world.controls.pointer.locked)
  const [buildMode, setBuildMode] = useState(world.builder.enabled)
  useEffect(() => {
    world.on('pointer-lock', setPointerLocked)
    world.on('build-mode', setBuildMode)
    return () => {
      world.off('pointer-lock', setPointerLocked)
      world.off('build-mode', setBuildMode)
    }
  }, [])
  const visible = isTouch ? true : pointerLocked
  if (!visible) return null
  return (
    <div
      className='reticle'
      css={css`
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        .reticle-item {
          width: 0.25rem;
          height: 0.25rem;
          border-radius: 0.625rem;
          /* border: 0.125rem solid ${buildMode ? '#ff4d4d' : 'white'}; */
          background: ${buildMode ? '#ff4d4d' : 'white'};
          border: 0.5px solid rgba(0, 0, 0, 0.3);
          /* mix-blend-mode: ${buildMode ? 'normal' : 'difference'}; */
        }
      `}
    >
      <div className='reticle-item' />
    </div>
  )
}

function Toast({ world }) {
  const [msg, setMsg] = useState(null)
  useEffect(() => {
    let ids = 0
    const onToast = text => {
      setMsg({ text, id: ++ids })
    }
    world.on('toast', onToast)
    return () => world.off('toast', onToast)
  }, [])
  if (!msg) return null
  return (
    <div
      className='toast'
      css={css`
        position: absolute;
        top: calc(50% - 4.375rem);
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        @keyframes toastIn {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .toast-msg {
          height: 2.875rem;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 1rem;
          background: rgba(11, 10, 21, 0.85);
          border: 0.0625rem solid #2a2b39;
          backdrop-filter: blur(5px);
          border-radius: 1.4375rem;
          opacity: 0;
          transform: translateY(0.625rem) scale(0.9);
          transition: all 0.1s ease-in-out;
          &.visible {
            opacity: 1;
            transform: translateY(0) scale(1);
            animation: toastIn 0.1s ease-in-out;
          }
        }
      `}
    >
      {msg && <ToastMsg key={msg.id} text={msg.text} />}
    </div>
  )
}

function ToastMsg({ text }) {
  const [visible, setVisible] = useState(true)
  useEffect(() => {
    setTimeout(() => setVisible(false), 1000)
  }, [])
  return <div className={cls('toast-msg', { visible })}>{text}</div>
}

function TouchBtns({ world }) {
  const [action, setAction] = useState(world.actions.current.node)
  useEffect(() => {
    function onChange(isAction) {
      setAction(isAction)
    }
    world.actions.on('change', onChange)
    return () => {
      world.actions.off('change', onChange)
    }
  }, [])
  return (
    <div
      className='touchbtns'
      css={css`
        position: absolute;
        top: calc(1.5rem + env(safe-area-inset-top));
        right: calc(1.5rem + env(safe-area-inset-right));
        bottom: calc(1.5rem + env(safe-area-inset-bottom));
        left: calc(1.5rem + env(safe-area-inset-left));
        .touchbtns-btn {
          pointer-events: auto;
          position: absolute;
          /* border: 1px solid rgba(255, 255, 255, 0.1); */
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10rem;
          display: flex;
          align-items: center;
          justify-content: center;
          &.jump {
            width: 4rem;
            height: 4rem;
            bottom: 1rem;
            right: 1rem;
          }
          &.action {
            width: 2.5rem;
            height: 2.5rem;
            bottom: 6rem;
            right: 4rem;
          }
        }
      `}
    >
      {action && (
        <div
          className='touchbtns-btn action'
          onPointerDown={e => {
            e.currentTarget.setPointerCapture(e.pointerId)
            world.controls.setTouchBtn('touchB', true)
          }}
          onPointerLeave={e => {
            world.controls.setTouchBtn('touchB', false)
            e.currentTarget.releasePointerCapture(e.pointerId)
          }}
        >
          <HandIcon size='1.5rem' />
        </div>
      )}
      <div
        className='touchbtns-btn jump'
        onPointerDown={e => {
          e.currentTarget.setPointerCapture(e.pointerId)
          world.controls.setTouchBtn('touchA', true)
        }}
        onPointerLeave={e => {
          world.controls.setTouchBtn('touchA', false)
          e.currentTarget.releasePointerCapture(e.pointerId)
        }}
      >
        <ChevronDoubleUpIcon size='1.5rem' />
      </div>
    </div>
  )
}

function TouchStick({ world }) {
  const outerRef = useRef()
  const innerRef = useRef()
  useEffect(() => {
    const outer = outerRef.current
    const inner = innerRef.current
    function onStick(stick) {
      if (stick) {
        outer.style.left = `${stick.center.x}px`
        outer.style.top = `${stick.center.y}px`
        inner.style.left = `${stick.touch.position.x}px`
        inner.style.top = `${stick.touch.position.y}px`
        inner.style.opacity = 1
      } else {
        inner.style.opacity = 0.1
        const radius = 50 // matches PlayerLocal.js STICK_OUTER_RADIUS
        if (window.innerWidth < window.innerHeight) {
          // portrait
          outer.style.left = `calc(env(safe-area-inset-left) + ${radius}px + 50px)`
          outer.style.top = `calc(100dvh - env(safe-area-inset-bottom) - ${radius}px - 50px)`
          inner.style.left = `calc(env(safe-area-inset-left) + ${radius}px + 50px)`
          inner.style.top = `calc(100dvh - env(safe-area-inset-bottom) - ${radius}px - 50px)`
        } else {
          // landscape
          outer.style.left = `calc(env(safe-area-inset-left) + ${radius}px + 90px)`
          outer.style.top = `calc(100dvh - env(safe-area-inset-bottom) - ${radius}px - 50px)`
          inner.style.left = `calc(env(safe-area-inset-left) + ${radius}px + 90px)`
          inner.style.top = `calc(100dvh - env(safe-area-inset-bottom) - ${radius}px - 50px)`
        }
      }
    }
    onStick(null)
    world.on('stick', onStick)
    return () => {
      world.off('stick', onStick)
    }
  }, [])
  return (
    <div
      className='stick'
      css={css`
        .stick-outer {
          position: absolute;
          width: 100px;
          height: 100px;
          border-radius: 100px;
          background: rgba(0, 0, 0, 0.3);
          transform: translate(-50%, -50%);
        }
        .stick-caret {
          position: absolute;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          &.n {
            top: 0;
            left: 50%;
            transform: translate(-50%, 0);
          }
          &.e {
            top: 50%;
            right: 0;
            transform: translate(0, -50%) rotate(90deg);
          }
          &.s {
            left: 50%;
            bottom: 0;
            transform: translate(-50%, 0) rotate(180deg);
          }
          &.w {
            top: 50%;
            left: 0;
            transform: translate(0, -50%) rotate(-90deg);
          }
        }
        .stick-inner {
          position: absolute;
          width: 50px;
          height: 50px;
          border-radius: 50px;
          background: white;
          transform: translate(-50%, -50%);
        }
      `}
    >
      <div className='stick-outer' ref={outerRef}>
        {/* <div className='stick-caret n'>
          <ChevronUpIcon size={16} />
        </div>
        <div className='stick-caret e'>
          <ChevronUpIcon size={16} />
        </div>
        <div className='stick-caret s'>
          <ChevronUpIcon size={16} />
        </div>
        <div className='stick-caret w'>
          <ChevronUpIcon size={16} />
        </div> */}
      </div>
      <div className='stick-inner' ref={innerRef} />
    </div>
  )
}

function Confirm({ options }) {
  return (
    <div
      className='confirm'
      css={css`
        position: absolute;
        inset: 0;
        padding: 1rem;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999;
        .confirm-dialog {
          pointer-events: auto;
          background: rgba(11, 10, 21, 0.9);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 1.375rem;
          backdrop-filter: blur(5px);
          width: 18rem;
        }
        .confirm-content {
          padding: 1.4rem;
        }
        .confirm-title {
          text-align: center;
          font-size: 1.1rem;
          font-weight: 500;
          margin: 0 0 0.7rem;
        }
        .confirm-message {
          text-align: center;
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.9375rem;
          line-height: 1.4;
        }
        .confirm-actions {
          border-top: 1px solid rgba(255, 255, 255, 0.05);
          display: flex;
          align-items: stretch;
        }
        .confirm-action {
          flex: 1;
          min-height: 2.7rem;
          display: flex;
          align-items: center;
          justify-content: center;
          &.left {
            border-right: 1px solid rgba(255, 255, 255, 0.05);
          }
          > span {
            font-size: 0.9375rem;
            color: rgba(255, 255, 255, 0.8);
          }
          &:hover {
            cursor: pointer;
            > span {
              color: white;
            }
          }
        }
      `}
    >
      <div className='confirm-dialog'>
        <div className='confirm-content'>
          <div className='confirm-title'>{options.title}</div>
          <div className='confirm-message'>{options.message}</div>
        </div>
        <div className='confirm-actions'>
          <div className='confirm-action left' onClick={options.confirm}>
            <span>{options.confirmText || 'Okay'}</span>
          </div>
          <div className='confirm-action' onClick={options.cancel}>
            <span>{options.cancelText || 'Cancel'}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
